import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type {
  PreviewExperiencePermissionRemovalQuery,
  PreviewExperiencePermissionRemovalQueryVariables,
} from './useRemovalPreview.generated';

// Sources this collaborator added that the caller cannot read themselves. Those
// stop delivering events the moment the collaborator is removed, because a
// source is held on behalf of whoever added it.
const query = gql`
  query PreviewExperiencePermissionRemoval(
    $input: PreviewExperiencePermissionRemovalInput!
  ) {
    previewExperiencePermissionRemoval(input: $input) {
      feedsGoingDark {
        id
        title
      }
    }
  }
`;

export const useRemovalPreview = (userId: string | null) => {
  const { experience } = usePageExperienceContext();

  const { data, isPending, isError } = useQuery({
    // Only asked once the dialog is open, so the roster costs nothing to render.
    enabled: userId !== null,
    queryKey: ['experience', experience.id, 'removal-preview', userId],
    queryFn: () =>
      gqlClient.request<
        PreviewExperiencePermissionRemovalQuery,
        PreviewExperiencePermissionRemovalQueryVariables
      >(query, {
        input: { experienceId: experience.id, userId: String(userId) },
      }),
  });

  return {
    feedsGoingDark:
      data?.previewExperiencePermissionRemoval.feedsGoingDark ?? [],
    isPending: userId !== null && isPending,
    // The check is advisory: on failure the dialog says so rather than
    // pretending nothing would be affected.
    isError,
  };
};
